import { motion } from "framer-motion";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { BOOKING_URL } from "@/lib/constants";

export default function Urgency() {
  return (
    <section
      id="urgency"
      data-testid="urgency-section"
      className="relative section-dark py-24 sm:py-32 overflow-hidden"
    >
      {/* Ambient gold pulse */}
      <motion.div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full gold-glow blur-3xl opacity-70"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 grid-lines opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#F5B800]" />
            <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#F5B800] flex items-center gap-2">
              <AlertTriangle size={12} /> 08 · Time-sensitive
            </span>
          </div>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            The window is open —
            <br />
            <span className="italic text-shimmer">but it&apos;s closing.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <motion.article
            data-testid="urgency-phase-1"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-md border border-[#F5B800] bg-[#0A1936]/60 backdrop-blur-md p-10"
          >
            <div className="absolute -top-3 left-8">
              <span className="font-mono uppercase text-[10px] tracking-[0.24em] bg-[#F5B800] text-[#0A1936] px-3 py-1 rounded-sm">
                Open Now
              </span>
            </div>
            <div className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#F5B800] mt-2">
              Phase 1
            </div>
            <h3 className="mt-3 font-serif text-white text-3xl leading-tight">
              CBP&apos;s CAPE portal is live
            </h3>
            <p className="mt-4 text-white/70 leading-relaxed">
              For unliquidated entries. This is the fastest path to refund —
              every week of delay is a week closer to losing recovery rights.
            </p>
          </motion.article>

          <motion.article
            data-testid="urgency-phase-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative rounded-md border border-white/10 bg-white/5 backdrop-blur-md p-10"
          >
            <div className="absolute -top-3 left-8">
              <span className="font-mono uppercase text-[10px] tracking-[0.24em] bg-white/10 border border-white/20 text-white/80 px-3 py-1 rounded-sm">
                No Confirmed Date
              </span>
            </div>
            <div className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/50 mt-2">
              Phase 2
            </div>
            <h3 className="mt-3 font-serif text-white text-3xl leading-tight">
              Liquidated entries — pending
            </h3>
            <p className="mt-4 text-white/70 leading-relaxed">
              CBP has not set a date. Companies that wait may miss this
              entirely. Preparation now positions you first in line.
            </p>
          </motion.article>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8 p-8 lg:p-10 rounded-md bg-white/5 border border-white/10 backdrop-blur-md"
        >
          <div>
            <div className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#F5B800]">
              Section 122 Tariffs
            </div>
            <div className="mt-2 font-serif text-white text-3xl sm:text-4xl italic">
              Expire July 23, 2026
            </div>
            <p className="mt-3 text-white/70 max-w-lg">
              IEEPA&apos;s replacement tariffs expire in weeks. The entire trade
              landscape is shifting. The assessment takes 15 minutes.
            </p>
          </div>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="urgency-cta"
            className="btn-magnetic shrink-0 inline-flex items-center gap-3 bg-[#F5B800] hover:bg-[#DDA600] text-[#0A1936] font-semibold text-base px-8 py-5 rounded-sm shadow-[0_10px_40px_-10px_rgba(245,184,0,0.6)] transition-colors"
          >
            Claim Your Refund
            <ArrowUpRight size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
